/**
 * Leveling Refinement Settings Module
 *
 * UI model:
 *   - Overbite is the master slider for the upper-arch defaults.
 *   - Each anterior tooth has a target "Incisal Edge Position (mm from
 *     occlusal plane, positive = into the bite)." Defaults are computed
 *     on the fly from Overbite + smile-arc table; manual overrides
 *     replace the default for individual teeth without affecting
 *     Overbite.
 *   - With "Break L/R symmetry" off (default), only three editable
 *     rows are shown per jaw (Central / Lateral / Canine); the value is
 *     applied to the mirrored FDI when sending to the backend.
 */

import { showToast } from "../modules/toast.js";
import { globalState } from "./globals.js";

// Order of the Break Symmetry override rows: laid out across the arch
// (canine -> central -> midline -> central -> canine) so the table reads left
// to right like the dentition. Upper: 13,12,11,21,22,23; Lower: 33,32,31,41,42,43.
const UPPER_FDIS = [13, 12, 11, 21, 22, 23];
const LOWER_FDIS = [33, 32, 31, 41, 42, 43];

// Right-side FDIs used as the "primary" when symmetry is on.
const UPPER_SYMMETRIC_PRIMARY = [11, 12, 13];
const LOWER_SYMMETRIC_PRIMARY = [31, 32, 33];

// Mirror pairs: right -> left and vice versa.
const MIRROR_PAIRS = {
    11: 21,
    21: 11,
    12: 22,
    22: 12,
    13: 23,
    23: 13,
    31: 41,
    41: 31,
    32: 42,
    42: 32,
    33: 43,
    43: 33
};

const TOOTH_LABEL = {
    11: "11 Central",
    21: "21 Central",
    12: "12 Lateral",
    22: "22 Lateral",
    13: "13 Canine",
    23: "23 Canine",
    31: "31 Central",
    41: "41 Central",
    32: "32 Lateral",
    42: "42 Lateral",
    33: "33 Canine",
    43: "43 Canine",
    // Symmetric (single-row) labels:
    "sym-central-upper": "Central\n(11/21)",
    "sym-lateral-upper": "Lateral\n(12/22)",
    "sym-canine-upper": "Canine\n(13/23)",
    "sym-central-lower": "Central\n(31/41)",
    "sym-lateral-lower": "Lateral\n(32/42)",
    "sym-canine-lower": "Canine\n(33/43)"
};

let MAX_SHIFT_MM = 10.0;

// Smile-arc offsets (overbite-independent, used to compute upper defaults).
// Loaded from /api/postprocessing-defaults on init.
let UPPER_SMILE_ARC = {
    11: 0.0,
    12: -0.299,
    13: 0.448,
    21: 0.0,
    22: -0.299,
    23: 0.448
};
let LOWER_SMILE_ARC = {
    31: 0.6,
    32: 0.606,
    33: 1.414,
    41: 0.6,
    42: 0.606,
    43: 1.414
};
let DEFAULT_OVERBITE_MM = 1.6;

// When False, the anterior-leveling activation checkbox is hidden and leveling
// is forced always-on (production). Controlled by the server setting
// ENABLE_LEVELING_ACTIVATION_TOGGLE via applyLevelingActivationToggle().
let levelingActivationToggleEnabled = true;

// Show/hide the leveling activation checkbox. When the toggle is disabled,
// leveling is always on: the checkbox itself is hidden and forced checked,
// and the controls are revealed. The "Anterior Leveling Refinement" label and
// tooltip stay visible either way - they are the section header and the
// visual boundary against Interproximal Refinement above, so hiding them
// along with the checkbox would make the two sections run together.
// collectLevelingParameters() and loadLevelingSettings() honor this flag so
// saved state cannot turn leveling off behind a hidden toggle.
export function applyLevelingActivationToggle(enabled) {
    levelingActivationToggleEnabled = enabled !== false;

    const enableCheckbox = document.getElementById("enable-leveling-checkbox");
    const controlsContainer = document.getElementById("leveling-controls");

    if (levelingActivationToggleEnabled) {
        if (enableCheckbox) {
            enableCheckbox.style.display = "";
        }
        return;
    }

    if (enableCheckbox) {
        enableCheckbox.style.display = "none";
        enableCheckbox.checked = true;
    }
    if (controlsContainer) {
        controlsContainer.style.display = "flex";
        controlsContainer.classList.remove("disabled");
        controlsContainer
            .querySelectorAll("input, select, button")
            .forEach((input) => {
                input.disabled = false;
            });
    }
}

// FDI used as the lower vertical reference for the overbite anchor. 31 and
// 41 share the same smile-arc value by construction.
const LOWER_CENTRAL_REF_FDI = 31;

// Upper incisors (responsive to Overbite slider).
const UPPER_INCISOR_FDIS = new Set([11, 12, 21, 22]);

function upperDefaultFor(fdi, overbiteMm) {
    // Upper incisors anchor scales with the Overbite slider.
    // Upper canines (13, 23) are clinically decoupled from Overbite -- their
    // anchor is pinned to the DEFAULT_OVERBITE_MM value so their default
    // position does not change when the slider moves.
    const lowerCentralRef = LOWER_SMILE_ARC[LOWER_CENTRAL_REF_FDI] ?? 0;
    const anchor = UPPER_INCISOR_FDIS.has(fdi)
        ? overbiteMm - lowerCentralRef
        : DEFAULT_OVERBITE_MM - lowerCentralRef;
    return anchor + (UPPER_SMILE_ARC[fdi] ?? 0);
}

function lowerDefaultFor(fdi) {
    return LOWER_SMILE_ARC[fdi] ?? 0;
}

function currentOverbite() {
    const slider = document.getElementById("overbite-mm-slider");
    return slider ? parseFloat(slider.value) : DEFAULT_OVERBITE_MM;
}

function isSymmetryBroken() {
    const cb = document.getElementById("leveling-break-symmetry-checkbox");
    return !!(cb && cb.checked);
}

async function loadDefaultSettingsFromServer() {
    try {
        const response = await fetch("/api/postprocessing-defaults");
        if (!response.ok) {
            return false;
        }
        const serverDefaults = await response.json();
        const lev = serverDefaults?.leveling;
        if (!lev) {
            return false;
        }
        DEFAULT_OVERBITE_MM = Number.isFinite(lev.overbite_mm)
            ? lev.overbite_mm
            : 1.6;
        if (lev.upper_smile_arc && typeof lev.upper_smile_arc === "object") {
            UPPER_SMILE_ARC = {};
            for (const [fdi, mm] of Object.entries(lev.upper_smile_arc)) {
                UPPER_SMILE_ARC[parseInt(fdi, 10)] = Number(mm);
            }
        }
        if (lev.lower_smile_arc && typeof lev.lower_smile_arc === "object") {
            LOWER_SMILE_ARC = {};
            for (const [fdi, mm] of Object.entries(lev.lower_smile_arc)) {
                LOWER_SMILE_ARC[parseInt(fdi, 10)] = Number(mm);
            }
        }
        if (Number.isFinite(lev.max_target_shift_mm)) {
            MAX_SHIFT_MM = lev.max_target_shift_mm;
        }
        return true;
    } catch (error) {
        console.warn(
            "Error loading leveling defaults from server:",
            error,
            "Using hardcoded fallbacks"
        );
        return false;
    }
}

function buildShiftRow(tbody, fdi, labelText) {
    const tr = document.createElement("tr");
    const tdFdi = document.createElement("td");
    // pre-line keeps explicit "\n" in the label (e.g. "Canine\n(13/23)") as a
    // line break so all rows wrap consistently regardless of column width.
    tdFdi.style.whiteSpace = "pre-line";
    tdFdi.textContent = labelText;
    const tdInput = document.createElement("td");
    const input = document.createElement("input");
    input.type = "number";
    input.step = "0.01";
    input.dataset.fdi = String(fdi);
    tdInput.appendChild(input);
    tr.appendChild(tdFdi);
    tr.appendChild(tdInput);
    tbody.appendChild(tr);
}

function rebuildShiftRows() {
    const upperTbody = document.getElementById("upper-relative-shifts-tbody");
    const lowerTbody = document.getElementById("lower-shifts-tbody");
    if (!upperTbody || !lowerTbody) {
        return;
    }
    const broken = isSymmetryBroken();
    upperTbody.innerHTML = "";
    lowerTbody.innerHTML = "";
    if (broken) {
        for (const fdi of UPPER_FDIS) {
            buildShiftRow(upperTbody, fdi, TOOTH_LABEL[fdi]);
        }
        for (const fdi of LOWER_FDIS) {
            buildShiftRow(lowerTbody, fdi, TOOTH_LABEL[fdi]);
        }
    } else {
        // Use the right-side FDI as the carrier; mirror at submit time.
        // Ordered canine -> lateral -> central to match the across-arch layout
        // of the Break Symmetry rows.
        buildShiftRow(upperTbody, 13, TOOTH_LABEL["sym-canine-upper"]);
        buildShiftRow(upperTbody, 12, TOOTH_LABEL["sym-lateral-upper"]);
        buildShiftRow(upperTbody, 11, TOOTH_LABEL["sym-central-upper"]);
        buildShiftRow(lowerTbody, 33, TOOTH_LABEL["sym-canine-lower"]);
        buildShiftRow(lowerTbody, 32, TOOTH_LABEL["sym-lateral-lower"]);
        buildShiftRow(lowerTbody, 31, TOOTH_LABEL["sym-central-lower"]);
    }
    refreshPlaceholders();
    attachShiftInputListeners();
}

function refreshPlaceholders() {
    const overbite = currentOverbite();
    document
        .querySelectorAll("#upper-relative-shifts-tbody input[type='number']")
        .forEach((input) => {
            const fdi = parseInt(input.dataset.fdi, 10);
            const def = upperDefaultFor(fdi, overbite);
            input.placeholder = `${def.toFixed(2)} (auto)`;
        });
    document
        .querySelectorAll("#lower-shifts-tbody input[type='number']")
        .forEach((input) => {
            const fdi = parseInt(input.dataset.fdi, 10);
            const def = lowerDefaultFor(fdi);
            input.placeholder = `${def.toFixed(2)} (auto)`;
        });
}

function clampShiftInput(input) {
    const raw = input.value.trim();
    if (raw === "") {
        return;
    }
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) {
        input.value = "";
        return;
    }
    if (Math.abs(value) > MAX_SHIFT_MM) {
        const clamped = Math.max(-MAX_SHIFT_MM, Math.min(MAX_SHIFT_MM, value));
        input.value = clamped.toFixed(2);
        showToast(
            `Position ${value.toFixed(
                2
            )}mm exceeds +-${MAX_SHIFT_MM}mm cap; clamped to ${clamped.toFixed(
                2
            )}mm`,
            "exclamation-triangle"
        );
    }
}

function attachShiftInputListeners() {
    document
        .querySelectorAll(
            "#upper-relative-shifts-tbody input[type='number'], #lower-shifts-tbody input[type='number']"
        )
        .forEach((input) => {
            input.addEventListener("change", () => {
                clampShiftInput(input);
                saveLevelingSettings();
            });
        });
}

export async function initializeLevelingControls() {
    const enableCheckbox = document.getElementById("enable-leveling-checkbox");
    const controlsContainer = document.getElementById("leveling-controls");

    if (!enableCheckbox || !controlsContainer) {
        return;
    }

    await loadDefaultSettingsFromServer();

    const overbiteSlider = document.getElementById("overbite-mm-slider");
    const overbiteValue = document.getElementById("overbite-mm-value");

    if (overbiteSlider && overbiteValue) {
        overbiteSlider.value = String(DEFAULT_OVERBITE_MM);
        overbiteValue.textContent = DEFAULT_OVERBITE_MM.toFixed(2);
        overbiteSlider.addEventListener("input", (e) => {
            overbiteValue.textContent = parseFloat(e.target.value).toFixed(2);
            refreshPlaceholders();
            saveLevelingSettings();
        });
    }

    const symmetryCb = document.getElementById(
        "leveling-break-symmetry-checkbox"
    );
    if (symmetryCb) {
        symmetryCb.addEventListener("change", () => {
            rebuildShiftRows();
            saveLevelingSettings();
        });
    }

    rebuildShiftRows();

    enableCheckbox.checked = true;
    loadLevelingSettings();

    function enableLevelingInputs(enabled) {
        const inputs = controlsContainer.querySelectorAll(
            "input, select, button"
        );
        inputs.forEach((input) => {
            input.disabled = !enabled;
        });
    }

    function toggleLevelingControls() {
        const isEnabled = enableCheckbox.checked;
        if (isEnabled) {
            controlsContainer.style.display = "flex";
            controlsContainer.classList.remove("disabled");
            enableLevelingInputs(true);
        } else {
            controlsContainer.style.display = "none";
            controlsContainer.classList.add("disabled");
            enableLevelingInputs(false);
        }
        saveLevelingSettings();
    }

    enableCheckbox.addEventListener("change", toggleLevelingControls);

    function toggleShiftsSection(checkboxId, containerId) {
        const cb = document.getElementById(checkboxId);
        const container = document.getElementById(containerId);
        if (!cb || !container) {
            return;
        }
        container.style.display = cb.checked ? "flex" : "none";
        saveLevelingSettings();
    }

    const upperShiftsCb = document.getElementById(
        "enable-upper-shifts-checkbox"
    );
    const lowerShiftsCb = document.getElementById(
        "enable-lower-shifts-checkbox"
    );
    if (upperShiftsCb) {
        upperShiftsCb.addEventListener("change", () =>
            toggleShiftsSection(
                "enable-upper-shifts-checkbox",
                "upper-shifts-controls"
            )
        );
    }
    if (lowerShiftsCb) {
        lowerShiftsCb.addEventListener("change", () =>
            toggleShiftsSection(
                "enable-lower-shifts-checkbox",
                "lower-shifts-controls"
            )
        );
    }

    const resetButton = document.getElementById("reset-leveling-button");
    if (resetButton) {
        resetButton.addEventListener("click", () => {
            resetLevelingSettings();
        });
    }

    toggleLevelingControls();
}

function readShiftOverrides(tbodyId) {
    // Read inputs from a tbody, expanding mirror-pairs when symmetry is on.
    const out = {};
    const tbody = document.getElementById(tbodyId);
    if (!tbody) {
        return out;
    }
    const broken = isSymmetryBroken();
    tbody.querySelectorAll("input[type='number']").forEach((input) => {
        const fdi = parseInt(input.dataset.fdi, 10);
        const raw = input.value.trim();
        if (!fdi || raw === "") {
            return;
        }
        const value = parseFloat(raw);
        if (!Number.isFinite(value)) {
            return;
        }
        out[fdi] = value;
        if (!broken) {
            const mirror = MIRROR_PAIRS[fdi];
            if (mirror) {
                out[mirror] = value;
            }
        }
    });
    return out;
}

export function collectLevelingParameters() {
    const enableCheckbox = document.getElementById("enable-leveling-checkbox");
    // When the activation toggle is hidden (production), leveling is always on
    // regardless of the (hidden) checkbox state.
    const isEnabled =
        !levelingActivationToggleEnabled ||
        (enableCheckbox && enableCheckbox.checked);
    if (!isEnabled) {
        return { enabled: false };
    }

    const overbiteSlider = document.getElementById("overbite-mm-slider");
    const resolveCollisionsCb = document.getElementById(
        "resolve-collisions-checkbox"
    );

    const params = {
        overbite_mm: parseFloat(overbiteSlider.value),
        resolve_collisions: resolveCollisionsCb
            ? resolveCollisionsCb.checked
            : true
    };

    const upperShiftsCb = document.getElementById(
        "enable-upper-shifts-checkbox"
    );
    const lowerShiftsCb = document.getElementById(
        "enable-lower-shifts-checkbox"
    );
    const upperTargets = upperShiftsCb?.checked
        ? readShiftOverrides("upper-relative-shifts-tbody")
        : {};
    const lowerTargets = lowerShiftsCb?.checked
        ? readShiftOverrides("lower-shifts-tbody")
        : {};
    if (Object.keys(upperTargets).length > 0) {
        params.upper_targets = upperTargets;
    }
    if (Object.keys(lowerTargets).length > 0) {
        params.lower_targets = lowerTargets;
    }

    return { enabled: true, parameters: params };
}

function readShiftRowsAsStrings(tbodyId) {
    const out = {};
    const tbody = document.getElementById(tbodyId);
    if (!tbody) {
        return out;
    }
    tbody.querySelectorAll("input[type='number']").forEach((input) => {
        const fdi = input.dataset.fdi;
        if (fdi) {
            out[fdi] = input.value;
        }
    });
    return out;
}

export function saveLevelingSettings() {
    try {
        const enableCheckbox = document.getElementById(
            "enable-leveling-checkbox"
        );
        const overbiteSlider = document.getElementById("overbite-mm-slider");
        if (!enableCheckbox || !overbiteSlider) {
            return;
        }
        const resolveCollisionsCb = document.getElementById(
            "resolve-collisions-checkbox"
        );
        const upperShiftsCb = document.getElementById(
            "enable-upper-shifts-checkbox"
        );
        const lowerShiftsCb = document.getElementById(
            "enable-lower-shifts-checkbox"
        );
        const symmetryCb = document.getElementById(
            "leveling-break-symmetry-checkbox"
        );
        const settings = {
            enabled: enableCheckbox.checked,
            overbite_mm: overbiteSlider.value,
            resolve_collisions: resolveCollisionsCb
                ? resolveCollisionsCb.checked
                : true,
            break_symmetry: !!(symmetryCb && symmetryCb.checked),
            upper_shifts_expanded: !!(upperShiftsCb && upperShiftsCb.checked),
            lower_shifts_expanded: !!(lowerShiftsCb && lowerShiftsCb.checked),
            upper_targets: readShiftRowsAsStrings(
                "upper-relative-shifts-tbody"
            ),
            lower_targets: readShiftRowsAsStrings("lower-shifts-tbody")
        };
        localStorage.setItem("levelingSettings", JSON.stringify(settings));
    } catch (error) {
        console.error("Error saving leveling settings:", error);
    }
}

function applyShiftValues(tbodyId, values) {
    const tbody = document.getElementById(tbodyId);
    if (!tbody || !values) {
        return;
    }
    tbody.querySelectorAll("input[type='number']").forEach((input) => {
        const fdi = input.dataset.fdi;
        if (fdi && Object.prototype.hasOwnProperty.call(values, fdi)) {
            input.value = values[fdi];
        }
    });
}

export function loadLevelingSettings() {
    try {
        const saved = localStorage.getItem("levelingSettings");
        if (!saved) {
            return;
        }
        const settings = JSON.parse(saved);
        const enableCheckbox = document.getElementById(
            "enable-leveling-checkbox"
        );
        const overbiteSlider = document.getElementById("overbite-mm-slider");
        const overbiteValue = document.getElementById("overbite-mm-value");

        if (
            enableCheckbox &&
            typeof settings.enabled === "boolean" &&
            levelingActivationToggleEnabled
        ) {
            enableCheckbox.checked = settings.enabled;
        }
        const resolveCollisionsCb = document.getElementById(
            "resolve-collisions-checkbox"
        );
        if (
            resolveCollisionsCb &&
            typeof settings.resolve_collisions === "boolean"
        ) {
            resolveCollisionsCb.checked = settings.resolve_collisions;
        }
        const symmetryCb = document.getElementById(
            "leveling-break-symmetry-checkbox"
        );
        if (symmetryCb && typeof settings.break_symmetry === "boolean") {
            symmetryCb.checked = settings.break_symmetry;
            rebuildShiftRows();
        }
        if (overbiteSlider && settings.overbite_mm != null) {
            overbiteSlider.value = settings.overbite_mm;
            if (overbiteValue) {
                overbiteValue.textContent = parseFloat(
                    settings.overbite_mm
                ).toFixed(2);
            }
            refreshPlaceholders();
        }
        applyShiftValues("upper-relative-shifts-tbody", settings.upper_targets);
        applyShiftValues("lower-shifts-tbody", settings.lower_targets);

        const upperShiftsCb = document.getElementById(
            "enable-upper-shifts-checkbox"
        );
        const lowerShiftsCb = document.getElementById(
            "enable-lower-shifts-checkbox"
        );
        const upperShiftsContainer = document.getElementById(
            "upper-shifts-controls"
        );
        const lowerShiftsContainer = document.getElementById(
            "lower-shifts-controls"
        );
        if (
            upperShiftsCb &&
            typeof settings.upper_shifts_expanded === "boolean"
        ) {
            upperShiftsCb.checked = settings.upper_shifts_expanded;
            if (upperShiftsContainer) {
                upperShiftsContainer.style.display =
                    settings.upper_shifts_expanded ? "flex" : "none";
            }
        }
        if (
            lowerShiftsCb &&
            typeof settings.lower_shifts_expanded === "boolean"
        ) {
            lowerShiftsCb.checked = settings.lower_shifts_expanded;
            if (lowerShiftsContainer) {
                lowerShiftsContainer.style.display =
                    settings.lower_shifts_expanded ? "flex" : "none";
            }
        }
    } catch (error) {
        console.error("Error loading leveling settings:", error);
    }
}

export function updateLevelingSectionVisibility() {
    // Visibility is governed by the parent postprocessing section.
}

export function resetLevelingSettings() {
    try {
        localStorage.removeItem("levelingSettings");

        const enableCheckbox = document.getElementById(
            "enable-leveling-checkbox"
        );
        const overbiteSlider = document.getElementById("overbite-mm-slider");
        const overbiteValue = document.getElementById("overbite-mm-value");

        if (enableCheckbox) {
            enableCheckbox.checked = true;
        }
        const resolveCollisionsCb = document.getElementById(
            "resolve-collisions-checkbox"
        );
        if (resolveCollisionsCb) {
            resolveCollisionsCb.checked = false;
        }
        if (overbiteSlider) {
            overbiteSlider.value = String(DEFAULT_OVERBITE_MM);
            if (overbiteValue) {
                overbiteValue.textContent = DEFAULT_OVERBITE_MM.toFixed(2);
            }
        }
        const symmetryCb = document.getElementById(
            "leveling-break-symmetry-checkbox"
        );
        if (symmetryCb) {
            symmetryCb.checked = false;
        }
        rebuildShiftRows();

        document
            .querySelectorAll(
                "#upper-relative-shifts-tbody input[type='number'], #lower-shifts-tbody input[type='number']"
            )
            .forEach((input) => {
                input.value = "";
            });

        const upperShiftsCb = document.getElementById(
            "enable-upper-shifts-checkbox"
        );
        const lowerShiftsCb = document.getElementById(
            "enable-lower-shifts-checkbox"
        );
        const upperShiftsContainer = document.getElementById(
            "upper-shifts-controls"
        );
        const lowerShiftsContainer = document.getElementById(
            "lower-shifts-controls"
        );
        if (upperShiftsCb) {
            upperShiftsCb.checked = false;
            if (upperShiftsContainer) {
                upperShiftsContainer.style.display = "none";
            }
        }
        if (lowerShiftsCb) {
            lowerShiftsCb.checked = false;
            if (lowerShiftsContainer) {
                lowerShiftsContainer.style.display = "none";
            }
        }

        const controlsContainer = document.getElementById("leveling-controls");
        if (controlsContainer) {
            controlsContainer.style.display = "flex";
            controlsContainer.classList.remove("disabled");
            const inputs = controlsContainer.querySelectorAll(
                "input, select, button"
            );
            inputs.forEach((input) => {
                input.disabled = false;
            });
        }

        showToast("Leveling settings reset to defaults", "check-circle");
    } catch (error) {
        console.error("Error resetting leveling settings:", error);
        showToast("Error resetting leveling settings", "exclamation-triangle");
    }
}

/**
 * Update the "Achieved overbite" read-only line shown next to the slider.
 * Call after a refinement run with the value reported by the backend
 * (stats.leveling_targets.achieved_overbite). Pass null/undefined to clear.
 */
export function updateAchievedOverbite(value) {
    const span = document.getElementById("achieved-overbite-value");
    if (!span) {
        return;
    }
    if (value == null || !Number.isFinite(value)) {
        span.textContent = "--";
    } else {
        span.textContent = `${Number(value).toFixed(2)} mm`;
    }
}

void globalState;
